if (!customElements.get('product-left-thumbs')) {
  /* The pre-order app (Smart Pre-order & Back in Stock) offers themes this hook
     to place its widgets itself; returning true takes that placement over. Its
     pre-order widget goes in the choice's own host and its notify widget in the
     back in stock slot, both of them elements this theme owns, so
     component-product-form.css can show either one without a rule that names
     the app's markup. Returning nothing leaves the app's own placement to run,
     which is what happens anywhere this theme has no host to offer. */
  const SMART_PREORDER_HOSTS = {
    preorder: '[data-plt-preorder-host]',
    restock: '[data-bis-placement="section"]',
  };

  const preorderHooks = (window.SMART_PREORDER = window.SMART_PREORDER || {});
  if (!preorderHooks.insertWidget) {
    preorderHooks.insertWidget = (root, info) => {
      const selector = SMART_PREORDER_HOSTS[info?.type];
      const scope = info?.form?.closest('product-left-thumbs');
      if (!selector || !scope) return undefined;

      const host = scope.querySelector(selector);
      if (!host) return undefined;

      if (root.parentNode !== host) host.appendChild(root);
      return true;
    };
  }

  class ProductLeftThumbs extends HTMLElement {
    connectedCallback() {
      // Set by the quick add popup, which renders the same form without being
      // the product page: it neither owns the URL nor counts as a product view.
      this.isEmbedded = this.hasAttribute('data-plt-embedded');

      this.form = this.querySelector('[data-plt-form]');
      this.thumbs = Array.from(this.querySelectorAll('[data-plt-thumb]'));
      this.mainFrame = this.querySelector('[data-plt-main-frame]');
      this.mainImage = this.querySelector('[data-plt-main-image]');
      this.counter = this.querySelector('[data-plt-counter]');
      this.prevArrow = this.querySelector('[data-plt-prev]');
      this.nextArrow = this.querySelector('[data-plt-next]');
      this.priceContainer = this.querySelector('[data-plt-price]');
      this.atcButton = this.querySelector('[data-plt-atc]');
      this.skuEl = this.querySelector('[data-plt-sku]');
      this.barcodeEl = this.querySelector('[data-plt-barcode]');
      this.availabilityEl = this.querySelector('[data-plt-availability]');
      this.idInput = this.querySelector('[data-plt-variant-id]');
      this.qtyInput = this.querySelector('[data-plt-qty-input]');
      // Carries the is-sold-out state for the whole buy area.
      this.cartGroup = this.querySelector('[data-plt-cart-group]');
      this.subtotalEl = this.querySelector('[data-plt-subtotal]');
      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
      this.currentPriceCents = parseInt(this.dataset.initialPriceCents, 10) || 0;

      this.recordRecentlyViewed();
      this.form?.addEventListener('submit', (event) => this.onFormSubmit(event));

      this.variants = [];
      const variantScript = this.querySelector('[data-plt-variants]');
      if (variantScript) {
        try {
          this.variants = JSON.parse(variantScript.textContent);
        } catch (error) {
          this.variants = [];
        }
      }

      // The variant the page opened on, so the purchase options picker and the
      // subtotal have a price to read before anything has been changed.
      this.currentVariant =
        this.variants.find((variant) => String(variant.id) === String(this.idInput?.value)) || null;

      this.purchaseOptions = this.querySelector('[data-plt-purchase-options]');
      if (this.purchaseOptions) this.initPurchaseOptions();

      this.preorderChoice = this.querySelector('[data-plt-preorder-choice]');
      if (this.preorderChoice) this.initPreorderChoice();

      this.activeIndex = this.thumbs.findIndex((thumb) => thumb.getAttribute('aria-current') === 'true');
      if (this.activeIndex < 0) this.activeIndex = 0;

      // Both optional: only present while the section's variant media filter is on.
      this.thumbsRail = this.querySelector('[data-plt-thumbs]');
      this.hasVariantGalleries = this.thumbs.some((thumb) => thumb.dataset.variantGallery);

      const filterData = window.VariantMediaFilter?.parse(this);
      this.mediaGroups = filterData ? window.VariantMediaFilter.build(filterData) : null;

      if (this.mediaGroups || this.hasVariantGalleries) {
        this.applyMediaFilter(filterData?.selectedVariantId || this.idInput?.value);
      }

      this.thumbs.forEach((thumb, index) => {
        thumb.addEventListener('click', () => this.setActiveThumb(index));
      });

      if (this.prevArrow) {
        this.prevArrow.addEventListener('click', () => this.step(-1));
      }
      if (this.nextArrow) {
        this.nextArrow.addEventListener('click', () => this.step(1));
      }

      if (this.mainFrame) {
        this.bindSwipe();
      }

      if (this.mainFrame && this.mainFrame.classList.contains('is-zoom-enabled')) {
        this.mainFrame.addEventListener('mousemove', (event) => this.handleZoom(event));
        this.mainFrame.addEventListener('mouseleave', () => {
          this.mainFrame.style.removeProperty('--zoom-x');
          this.mainFrame.style.removeProperty('--zoom-y');
        });
      }

      if (this.form) {
        this.form.addEventListener('change', (event) => {
          if (event.target.matches('[data-plt-option]')) this.handleOptionChange();
        });
      }

      const decrement = this.querySelector('[data-plt-qty-decrement]');
      const increment = this.querySelector('[data-plt-qty-increment]');
      if (decrement && this.qtyInput) {
        decrement.addEventListener('click', () => this.stepQuantity(-1));
      }
      if (increment && this.qtyInput) {
        increment.addEventListener('click', () => this.stepQuantity(1));
      }
      if (this.qtyInput) {
        this.qtyInput.addEventListener('change', () => this.updateSubtotal());
        this.qtyInput.addEventListener('input', () => this.updateSubtotal());
      }

      const reviewLink = this.querySelector('[data-plt-review-scroll]');
      if (reviewLink) {
        reviewLink.addEventListener('click', (event) => this.scrollToReviews(event));
      }

      const sizeChartOpen = this.querySelector('[data-plt-size-chart-open]');
      const sizeChartDialog = this.querySelector('[data-plt-size-chart-dialog]');
      const sizeChartClose = this.querySelector('[data-plt-size-chart-close]');
      if (sizeChartOpen && sizeChartDialog && typeof sizeChartDialog.showModal === 'function') {
        sizeChartOpen.addEventListener('click', () => sizeChartDialog.showModal());
      }
      if (sizeChartClose && sizeChartDialog) {
        sizeChartClose.addEventListener('click', () => sizeChartDialog.close());
      }

      this.updateCounter();
      this.updateSubtotal();
    }

    /* Add to cart
       Adds through the Cart AJAX API and hands the response to the theme's own
       cart-drawer component, so the drawer opens with the new line instead of the
       browser posting the form and landing on the cart page. With no drawer on the
       page (cart type set to page, or the section removed) the form is left alone
       and posts as it always did. */

    cartDrawer() {
      const drawer = document.querySelector('cart-drawer');
      return drawer && typeof drawer.renderContents === 'function' ? drawer : null;
    }

    onFormSubmit(event) {
      const drawer = this.cartDrawer();
      if (!drawer) return;

      event.preventDefault();

      if (this.atcButton?.getAttribute('aria-disabled') === 'true') return;

      this.setAddToCartLoading(true);
      this.showFormError('');

      const formData = new FormData(this.form);
      formData.append('sections', drawer.getSectionsToRender().map((section) => section.id).join(','));
      formData.append('sections_url', window.location.pathname);
      drawer.setActiveElement?.(this.atcButton);

      const config =
        typeof fetchConfig === 'function'
          ? fetchConfig('javascript')
          : { method: 'POST', headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' } };
      delete config.headers['Content-Type'];
      config.body = formData;

      fetch(window.routes?.cart_add_url || '/cart/add', config)
        .then((response) => response.json())
        .then((response) => {
          // The Cart API answers an error with a status field and no line items.
          if (response.status) {
            this.showFormError(response.description || response.message || '');
            return;
          }

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-left-thumbs',
              productVariantId: formData.get('id'),
              cartData: response,
            });
          }

          drawer.renderContents(response);
        })
        .catch(() => {
          // Network failure: fall back to the plain form post rather than
          // leaving the shopper with a button that did nothing.
          this.form.submit();
        })
        .finally(() => this.setAddToCartLoading(false));
    }

    setAddToCartLoading(isLoading) {
      if (!this.atcButton) return;

      this.atcButton.classList.toggle('is-loading', isLoading);

      if (isLoading) {
        this.atcButton.setAttribute('aria-disabled', 'true');
      } else {
        this.atcButton.removeAttribute('aria-disabled');
      }
    }

    showFormError(message) {
      const errorEl = this.querySelector('[data-plt-form-error]');
      if (!errorEl) return;

      errorEl.textContent = message;
      errorEl.hidden = message === '';
    }

    /* Recently viewed
       Same key and shape as product-info.js writes on the theme's other product
       templates, so both recently viewed sections read one list. */

    recordRecentlyViewed() {
      if (this.isEmbedded) return;

      const name = '_halo_recently_viewed';
      const productId = parseInt(this.dataset.productId, 10);
      if (!productId) return;

      try {
        const stored = JSON.parse(window.localStorage.getItem(name) || '[]');
        const list = Array.isArray(stored) ? stored.filter((id) => id !== productId) : [];
        list.unshift(productId);
        window.localStorage.setItem(name, JSON.stringify(list.slice(0, 25)));
      } catch (error) {
        // Private mode and blocked site data both throw; the page must not break.
      }
    }

    /* Review rating link
       Judge.me names its review-list widget consistently across installs (id
       "judgeme_product_reviews", falling back to its "jdgm-review-widget" class)
       regardless of which product template or section id placed it, so the same
       selector works everywhere this component is used rather than one tied to a
       specific section's generated id. */

    scrollToReviews(event) {
      const target =
        document.getElementById('judgeme_product_reviews') ||
        document.querySelector('.jdgm-review-widget');
      if (!target) return;

      event.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    updateSubtotal() {
      if (!this.subtotalEl || !this.qtyInput) return;
      const quantity = parseInt(this.qtyInput.value, 10) || 1;
      const total = this.unitPriceCents() * quantity;

      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        this.subtotalEl.textContent = window.Shopify.formatMoney(total, this.moneyFormat);
      }
    }

    /* Purchase options
       A one-time purchase against the product's own subscription plans. The
       picker writes the chosen plan to the form's selling_plan input (empty for
       a one-time purchase) and takes its prices from the variant's selling plan
       allocations in the variants JSON, so what is shown is what Shopify charges
       rather than a discount recalculated here. */

    initPurchaseOptions() {
      this.sellingPlanInput = this.purchaseOptions.querySelector('[data-plt-selling-plan]');
      this.purchaseCards = Array.from(this.purchaseOptions.querySelectorAll('[data-plt-purchase-card]'));

      // Each says which stock state it belongs to. Empty on a product with only
      // one set of options to show, which is how every card stays on screen.
      this.stockModeCards = this.purchaseCards.filter((card) => card.dataset.pltStockMode);

      this.purchaseOptions.addEventListener('change', (event) => {
        if (event.target.matches('[data-plt-plan-select]')) {
          this.selectPlan(event.target);
        } else if (event.target.matches('[data-plt-purchase-radio]')) {
          this.selectPurchaseOption(event.target);
        }
      });

      this.purchaseOptions.addEventListener('click', (event) => {
        const toggle = event.target.closest('[data-plt-purchase-info]');
        if (toggle) this.togglePurchaseDetails(toggle);
      });

      // A popover closes on the next click away from it, and on Escape, the way
      // the rest of the page dismisses what it opens.
      this.onDocumentClick = (event) => {
        if (event.target.closest('[data-plt-purchase-info]')) return;
        if (event.target.closest('[data-plt-purchase-details]')) return;
        this.closePurchaseDetails();
      };
      this.onDocumentKeydown = (event) => {
        if (event.key === 'Escape') this.closePurchaseDetails();
      };
      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('keydown', this.onDocumentKeydown);

      const checked = this.purchaseOptions.querySelector('[data-plt-purchase-radio]:checked');
      if (checked) this.selectPurchaseOption(checked);
    }

    // The quick add popup creates and drops this component repeatedly, so the
    // document listeners it added leave with it.
    disconnectedCallback() {
      if (this.onDocumentClick) document.removeEventListener('click', this.onDocumentClick);
      if (this.onDocumentKeydown) document.removeEventListener('keydown', this.onDocumentKeydown);
      this.preorderWatcher?.disconnect();
    }

    selectPurchaseOption(radio) {
      const card = radio.closest('[data-plt-purchase-card]');

      // A card with several frequencies carries the chosen one on its select, so
      // the radio always holds the plan that would be added to the cart.
      const select = card ? card.querySelector('[data-plt-plan-select]') : null;
      if (select) radio.value = select.value;

      this.purchaseCards.forEach((el) => el.classList.toggle('is-selected', el === card));
      if (this.sellingPlanInput) this.sellingPlanInput.value = radio.value || '';

      this.updateSubtotal();
    }

    selectPlan(select) {
      const card = select.closest('[data-plt-purchase-card]');
      const radio = card ? card.querySelector('[data-plt-purchase-radio]') : null;
      if (!radio) return;

      // Reaching for the frequency of a card that is not selected means picking
      // that card, so it is selected rather than left out of the cart.
      radio.checked = true;
      this.selectPurchaseOption(radio);
      this.updatePurchasePrices();
    }

    // Each card shows the price of the plan it would add, for the variant on screen.
    updatePurchasePrices() {
      if (!this.purchaseOptions || !this.currentVariant) return;

      this.purchaseCards.forEach((card) => {
        const priceEl = card.querySelector('[data-plt-po-price]');
        if (!priceEl) return;

        const select = card.querySelector('[data-plt-plan-select]');
        const radio = card.querySelector('[data-plt-purchase-radio]');
        const planId = select ? select.value : radio?.value;
        const allocation = this.planAllocation(planId);

        priceEl.textContent = allocation ? allocation.price : this.currentVariant.price;
      });
    }

    /* Buying from stock and pre-ordering are alternatives, so the picker shows
       one set of cards or the other: one-time and subscription while the variant
       has stock, the pre-order plans alone while it has none.

       Read from `stocked` rather than `available`: a pre-order campaign keeps
       the variant on sale at zero inventory, so it is available and out of
       stock at the same time. The server renders the same state for the variant
       the page opens on. */

    updateStockOptions(variant) {
      if (!this.stockModeCards.length) return;

      // An option combination that resolves to no variant is not a pre-order
      // either, and neither is one whose data predates the `stocked` key, so
      // both fall back to the in-stock set.
      const wanted = variant?.stocked === false ? 'out-of-stock' : 'in-stock';
      let hidTheChoice = false;

      this.stockModeCards.forEach((card) => {
        const hide = card.dataset.pltStockMode !== wanted;
        if (hide && card.querySelector('[data-plt-purchase-radio]:checked')) {
          hidTheChoice = true;
        }
        card.hidden = hide;
      });

      // A hidden card must not stay the one that would be added to the cart, so
      // the swap hands the choice to the first card of the set now on screen.
      if (!hidTheChoice) return;

      const fallback = this.purchaseCards
        .find((card) => !card.hidden)
        ?.querySelector('[data-plt-purchase-radio]');
      if (!fallback) return;

      fallback.checked = true;
      this.selectPurchaseOption(fallback);
    }

    // Null for a one-time purchase, and for a plan the variant is not sold on.
    planAllocation(planId) {
      const plans = this.currentVariant?.selling_plan_prices;
      if (!planId || !plans) return null;
      return plans[planId] || null;
    }

    // The price a line would be added at: the plan's while a subscription is
    // selected, the variant's otherwise.
    unitPriceCents() {
      const allocation = this.planAllocation(this.sellingPlanInput?.value);
      return allocation ? allocation.price_cents : this.currentPriceCents;
    }

    /* Hover and keyboard focus open the popover from CSS. This is the touch path,
       where there is no hover: the class it toggles is the second way the
       stylesheet opens one. */

    togglePurchaseDetails(button) {
      const wrap = button.closest('.purchase-options__details-wrap');
      if (!wrap) return;

      const isOpen = wrap.classList.contains('is-open');
      this.closePurchaseDetails();
      if (isOpen) return;

      wrap.classList.add('is-open');
      button.setAttribute('aria-expanded', 'true');
    }

    closePurchaseDetails() {
      if (!this.purchaseOptions) return;

      this.purchaseOptions.querySelectorAll('.purchase-options__details-wrap.is-open').forEach((wrap) => {
        wrap.classList.remove('is-open');
        wrap.querySelector('[data-plt-purchase-info]')?.setAttribute('aria-expanded', 'false');
      });
    }

    /* Pre-order choice
       A sold-out variant may still be on a pre-order campaign. Which of the two
       the shopper wants - the pre-order or a note when it is back - is the
       theme's question to ask; whether a pre-order exists at all is the app's
       to answer, so the only thing read here is the signal the app itself
       treats as "a pre-order is live": the selling plan input it plants in the
       form. Nothing of the app's own is written to or restyled. */

    initPreorderChoice() {
      this.preorderModes = Array.from(this.preorderChoice.querySelectorAll('[data-plt-preorder-mode]'));

      this.preorderChoice.addEventListener('change', (event) => {
        if (event.target.matches('[data-plt-preorder-mode]')) this.updatePreorderChoice();
      });

      // The app plants its input once its own scripts have run, and plants it
      // again on every variant change, so the form is watched rather than read
      // the once. Only childList: the classes and attributes this sets in
      // answer are not mutations it will hear back.
      this.preorderWatcher = new MutationObserver(() => this.updatePreorderChoice());
      if (this.form) this.preorderWatcher.observe(this.form, { childList: true, subtree: true });

      this.updatePreorderChoice();
    }

    // The app's own test for a live pre-order on the variant now in the form.
    hasLivePreorder() {
      return !!this.form?.querySelector('input.etp-sp, input.etp-sp-prop');
    }

    // Offered only where both halves hold: the variant cannot be bought, and
    // the app has a pre-order for it. A sold-out variant with no campaign keeps
    // the back in stock button it has always shown on its own, and pre-ordering
    // is never the state of a variant that is not being offered one.
    updatePreorderChoice() {
      if (!this.preorderChoice) return;

      const soldOut = this.cartGroup?.classList.contains('is-sold-out');
      const offered = !!soldOut && this.hasLivePreorder();
      const chosen = this.preorderModes?.find((input) => input.checked);
      const preordering = offered && chosen?.value === 'pre-order';

      // Both are written only when they change. The app watches this buy area
      // for mutations of its own, and a write of the value already there is
      // still a mutation it would answer.
      if (this.preorderChoice.hidden === offered) this.preorderChoice.hidden = !offered;

      if (this.cartGroup && this.cartGroup.classList.contains('is-preorder') !== preordering) {
        this.cartGroup.classList.toggle('is-preorder', preordering);
      }
    }

    stepQuantity(delta) {
      const min = parseInt(this.qtyInput.min, 10) || 1;
      const current = parseInt(this.qtyInput.value, 10) || min;
      const next = Math.max(min, current + delta);
      this.qtyInput.value = next;
      this.qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /* Variant media filter
       Media the selected variant does not own is hidden rather than removed, so a
       later variant can show it again without the gallery being rebuilt. Everything
       that walks the gallery works off the visible thumbs from here on.

       A variant that carries its own images in the variant gallery metafield shows
       exactly those. Anything else falls back to filtering the product's own media
       by image alt text or by media order. */

    visibleThumbs() {
      return this.thumbs.filter((thumb) => !thumb.hidden);
    }

    applyMediaFilter(variantId) {
      if (!this.mediaGroups && !this.hasVariantGalleries) return;

      const ownGallery = this.hasVariantGalleries
        ? this.thumbs.filter((thumb) => thumb.dataset.variantGallery === String(variantId))
        : [];

      if (ownGallery.length) {
        this.thumbs.forEach((thumb) => {
          thumb.hidden = !ownGallery.includes(thumb);
        });
      } else {
        const visible = this.mediaGroups
          ? window.VariantMediaFilter.visibleFor(this.mediaGroups, variantId)
          : null;

        this.thumbs.forEach((thumb) => {
          // Another variant's gallery never belongs in the product's own media.
          if (thumb.dataset.variantGallery) {
            thumb.hidden = true;
            return;
          }

          thumb.hidden = Boolean(visible) && !visible.has(thumb.dataset.mediaId);
        });
      }

      const remaining = this.visibleThumbs();

      // One image left has nothing to page through, pick from, or count.
      const isSingle = remaining.length < 2;
      this.thumbsRail?.toggleAttribute('hidden', isSingle);
      this.prevArrow?.toggleAttribute('hidden', isSingle);
      this.nextArrow?.toggleAttribute('hidden', isSingle);
      this.counter?.toggleAttribute('hidden', isSingle);

      if (this.thumbs[this.activeIndex]?.hidden && remaining.length) {
        this.setActiveThumb(this.thumbs.indexOf(remaining[0]));
        return;
      }

      this.updateCounter();
    }

    /**
     * Pages the gallery by dragging across the main image, so touch devices have a
     * control of their own and the arrows can stay hidden until a pointer hovers them.
     *
     * Mouse pointers are skipped: on desktop the arrows are there on hover, and the
     * frame may already be using the mouse for zoom.
     */
    bindSwipe() {
      // Below this a drag reads as a tap, or as the small sideways drift of a scroll.
      const SWIPE_THRESHOLD = 40;

      let startX = 0;
      let startY = 0;
      let tracking = false;

      // Listeners stay passive so a vertical drag still scrolls the page normally; the
      // browser then takes the gesture over and fires pointercancel, which ends
      // tracking. Touch pointers are implicitly captured on pointerdown, so pointerup
      // still lands here even when the finger lifts past the edge of the image.
      this.mainFrame.addEventListener(
        'pointerdown',
        (event) => {
          if (event.pointerType === 'mouse') return;
          startX = event.clientX;
          startY = event.clientY;
          tracking = true;
        },
        { passive: true }
      );

      this.mainFrame.addEventListener(
        'pointerup',
        (event) => {
          if (!tracking) return;
          tracking = false;

          const deltaX = event.clientX - startX;
          const deltaY = event.clientY - startY;

          if (Math.abs(deltaX) < SWIPE_THRESHOLD) return;
          // A drag with more vertical than horizontal travel was aiming down the page.
          if (Math.abs(deltaX) <= Math.abs(deltaY)) return;

          this.step(deltaX < 0 ? 1 : -1);
        },
        { passive: true }
      );

      this.mainFrame.addEventListener(
        'pointercancel',
        () => {
          tracking = false;
        },
        { passive: true }
      );
    }

    step(delta) {
      const visible = this.visibleThumbs();
      if (!visible.length) return;

      const current = Math.max(visible.indexOf(this.thumbs[this.activeIndex]), 0);
      const next = (current + delta + visible.length) % visible.length;
      this.setActiveThumb(this.thumbs.indexOf(visible[next]));
    }

    setActiveThumb(index) {
      const thumb = this.thumbs[index];
      if (!thumb) return;

      this.thumbs.forEach((el) => el.setAttribute('aria-current', 'false'));
      thumb.setAttribute('aria-current', 'true');
      this.activeIndex = index;

      if (this.mainImage) {
        const thumbImage = thumb.querySelector('img');
        this.swapMainImage(thumb.dataset.fullSrc, thumb.dataset.fullSrcset, thumbImage?.alt);
      }

      thumb.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
      this.updateCounter();
    }

    /* Gallery swap
       The frame fades out, the image is replaced while it cannot be seen, and it
       fades back in. Waiting on both the fade and the download keeps a slow image
       from fading in half loaded. */

    swapMainImage(src, srcset, alt) {
      const apply = () => {
        // A stale srcset wins over src, so it has to be replaced (or dropped)
        // before the new src can take effect.
        if (srcset) {
          this.mainImage.srcset = srcset;
        } else {
          this.mainImage.removeAttribute('srcset');
        }

        if (src) this.mainImage.src = src;
        if (alt != null) this.mainImage.alt = alt;
      };

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (!this.mainFrame || reduceMotion) {
        apply();
        return;
      }

      // Rapid clicks: only the newest swap is allowed to finish.
      this.swapToken = (this.swapToken || 0) + 1;
      const token = this.swapToken;

      this.mainFrame.classList.add('is-swapping');

      Promise.all([this.preloadImage(src, srcset), this.wait(150)]).then(() => {
        if (token !== this.swapToken) return;
        apply();
        this.mainFrame.classList.remove('is-swapping');
      });
    }

    preloadImage(src, srcset) {
      return new Promise((resolve) => {
        if (!src && !srcset) {
          resolve();
          return;
        }

        const image = new Image();
        if (srcset) image.srcset = srcset;
        if (src) image.src = src;

        if (image.complete) {
          resolve();
          return;
        }

        image.addEventListener('load', resolve, { once: true });
        image.addEventListener('error', resolve, { once: true });
        // A load event that never arrives must not leave the frame faded out.
        window.setTimeout(resolve, 600);
      });
    }

    wait(duration) {
      return new Promise((resolve) => window.setTimeout(resolve, duration));
    }

    setActiveThumbByMediaId(mediaId) {
      if (!mediaId) return;
      const index = this.thumbs.findIndex(
        (thumb) => thumb.dataset.mediaId === String(mediaId) && !thumb.hidden
      );
      if (index >= 0) this.setActiveThumb(index);
    }

    updateCounter() {
      const visible = this.visibleThumbs();
      if (!this.counter || !visible.length) return;

      const position = visible.indexOf(this.thumbs[this.activeIndex]) + 1;
      this.counter.textContent = `${position || 1} / ${visible.length}`;
    }

    handleZoom(event) {
      const rect = this.mainFrame.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width) * 100;
      const y = ((event.clientY - rect.top) / rect.height) * 100;
      this.mainFrame.style.setProperty('--zoom-x', `${x}%`);
      this.mainFrame.style.setProperty('--zoom-y', `${y}%`);
    }

    handleOptionChange() {
      const optionInputs = Array.from(this.querySelectorAll('[data-plt-option]'));
      const selected = [];
      const seen = new Set();

      optionInputs.forEach((input) => {
        const position = input.dataset.optionPosition;
        if (seen.has(position)) return;
        if (input.tagName === 'SELECT') {
          selected[position] = input.value;
          seen.add(position);
        } else if (input.type === 'radio' && input.checked) {
          selected[position] = input.value;
          seen.add(position);
        }
      });

      const matched = this.variants.find((variant) =>
        variant.options.every((value, index) => value === selected[index])
      );

      this.applyVariant(matched);
    }

    applyVariant(variant) {
      if (!this.idInput) return;

      if (!variant) {
        this.idInput.value = '';
        if (this.atcButton) {
          this.atcButton.disabled = true;
          this.atcButton.textContent = this.atcButton.dataset.unavailableText || this.atcButton.textContent;
        }
        // A combination that resolves to no variant has nothing to notify about
        // either, so the disabled Unavailable button is shown rather than an
        // empty row left over from a sold-out variant.
        this.cartGroup?.classList.remove('is-sold-out');
        this.updateStockOptions(null);
        this.updatePreorderChoice();
        return;
      }

      this.idInput.value = variant.id;

      // Embedded instances (the quick add popup) are not the page the address
      // bar is describing, so they leave the URL alone.
      if (!this.isEmbedded && window.history && window.history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }

      if (this.priceContainer) {
        const regular = this.priceContainer.querySelector('[data-plt-price-regular]');
        const compare = this.priceContainer.querySelector('[data-plt-price-compare]');
        const save = this.priceContainer.querySelector('[data-plt-price-save]');
        const each = this.priceContainer.querySelector('[data-plt-price-each]');
        if (regular) regular.textContent = variant.price;
        if (compare) {
          if (variant.compare_at_price) {
            compare.textContent = variant.compare_at_price;
            compare.hidden = false;
          } else {
            compare.hidden = true;
          }
        }
        // Both are null unless the variant itself is discounted, so an
        // undiscounted variant never inherits the previous one's savings.
        if (save) {
          if (variant.savings) {
            save.textContent = variant.savings;
            save.hidden = false;
          } else {
            save.textContent = '';
            save.hidden = true;
          }
        }
        // Null whenever the per-unit figure could not be derived for a variant.
        if (each) {
          if (variant.unit_each) {
            each.textContent = variant.unit_each;
            each.hidden = false;
          } else {
            each.textContent = '';
            each.hidden = true;
          }
        }
      }

      this.updateOptionLabels(variant);

      if (this.skuEl) this.skuEl.textContent = variant.sku || '';
      if (this.barcodeEl) this.barcodeEl.textContent = variant.barcode || '';

      this.currentVariant = variant;
      this.currentPriceCents = variant.price_cents || 0;
      this.updateStockOptions(variant);
      this.updatePurchasePrices();
      this.updateSubtotal();

      if (this.availabilityEl) {
        this.availabilityEl.textContent = variant.available
          ? this.availabilityEl.dataset.inStockText
          : this.availabilityEl.dataset.outOfStockText;
      }

      if (this.atcButton) {
        this.atcButton.disabled = !variant.available;
        this.atcButton.textContent = variant.available
          ? this.atcButton.dataset.addToCartText
          : this.atcButton.dataset.soldOutText;
      }

      // Hands the buy area over to the back in stock app's button whenever the
      // variant on screen cannot be bought. The styling lives in
      // component-product-form.css; this only says which state the row is in.
      this.cartGroup?.classList.toggle('is-sold-out', !variant.available);
      this.updatePreorderChoice();

      // Filtered first, so the media this variant owns is on screen to switch to.
      this.applyMediaFilter(variant.id);
      this.setActiveThumbByMediaId(variant.featured_media_id);
    }

    // Keeps the "Option: value" labels in step with the selected variant.
    updateOptionLabels(variant) {
      this.querySelectorAll('[data-plt-option-value]').forEach((el) => {
        const position = parseInt(el.dataset.optionPosition, 10);
        const value = variant.options[position];
        if (value !== undefined) el.textContent = value;
      });
    }
  }

  customElements.define('product-left-thumbs', ProductLeftThumbs);
}
